import * as React from 'react';
import {
  ListBox,
  ListBoxItem,
  Popover,
  Button as RACButton,
} from 'react-aria-components';
import { DownSmallLine, CheckLine, CloseLine } from '@mingcute/react';

import { cn } from '../lib/cn';

/**
 * Klaret MultiSelect (handoff §10.3).
 * Chosen values render as r-1 tags in the trigger (overflow "+N").
 * Uses a plain controlled popover with RAC ListBox (selectionMode="multiple").
 * Focus = global 2 px ink outline. Accent border on focus (text-field rule).
 */

export interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value?: string[];
  onValueChange?: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
  maxSelected?: number;
  id?: string;
}

export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      value = [],
      onValueChange,
      placeholder = 'Select…',
      searchPlaceholder = 'Search…',
      emptyMessage = 'No results.',
      invalid,
      disabled,
      className,
      maxSelected,
      id,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);

    // Merge forwarded ref with internal ref
    const setRef = React.useCallback(
      (node: HTMLButtonElement | null) => {
        triggerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
      },
      [ref],
    );

    const toggle = (v: string) => {
      const next = value.includes(v)
        ? value.filter((x) => x !== v)
        : maxSelected && value.length >= maxSelected
          ? value
          : [...value, v];
      onValueChange?.(next);
    };

    const remove = (v: string, e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      onValueChange?.(value.filter((x) => x !== v));
    };

    // Defensive: a shared primitive must never crash the whole page when an
    // option arrives with a nullish label (e.g. a backend shape mismatch).
    // Degrade to the value as the display fallback instead of throwing.
    const labelOf = (o: MultiSelectOption) => String(o.label ?? o.value ?? '');
    const filteredOptions = options.filter((o) =>
      labelOf(o).toLowerCase().includes(search.toLowerCase()),
    );

    // Overflow: show max 3 tags, then "+N"
    const MAX_TAGS = 3;
    const visibleSelected = value.slice(0, MAX_TAGS);
    const overflowCount = value.length > MAX_TAGS ? value.length - MAX_TAGS : 0;

    return (
      <div className={cn('relative flex w-full flex-col', className)}>
        {/* Trigger */}
        <button
          ref={setRef}
          id={id}
          type="button"
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => !disabled && setOpen((o) => !o)}
          className={cn(
            'flex min-h-9 w-full flex-wrap items-center gap-1 rounded-[var(--tvl-r2)]',
            'border border-[var(--tvl-border-input)] bg-[var(--tvl-raised)] px-2 py-1 text-sm',
            'transition-colors duration-[var(--tvl-d-fast)]',
            'hover:border-[var(--tvl-ink-2)]',
            'focus-visible:outline-[2px] focus-visible:outline-solid focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
            invalid && 'border-[var(--tvl-danger)]',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          {value.length === 0 ? (
            <span className="px-1 text-sm text-[var(--tvl-ink-3)]">{placeholder}</span>
          ) : (
            <>
              {visibleSelected.map((v) => {
                const opt = options.find((o) => o.value === v);
                return (
                  <span
                    key={v}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-[var(--tvl-r1)]',
                      'bg-[var(--tvl-selected)] px-1.5 py-0.5 text-xs',
                      'font-medium text-[var(--tvl-accent)]',
                    )}
                  >
                    {opt?.label ?? v}
                    <span
                      role="button"
                      tabIndex={-1}
                      onMouseDown={(e) => remove(v, e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') remove(v, e);
                      }}
                      aria-label={`Remove ${opt?.label ?? v}`}
                      className="cursor-pointer text-[var(--tvl-accent)] hover:text-[var(--tvl-accent-hover)] outline-none"
                    >
                      <CloseLine size={12} aria-hidden />
                    </span>
                  </span>
                );
              })}
              {overflowCount > 0 && (
                <span className="inline-flex items-center rounded-[var(--tvl-r1)] bg-[var(--tvl-sunken)] px-1.5 py-0.5 text-xs text-[var(--tvl-ink-2)]">
                  +{overflowCount}
                </span>
              )}
            </>
          )}
          <DownSmallLine
            size={16}
            color="var(--tvl-ink-3)"
            aria-hidden
            className="ml-auto shrink-0"
          />
        </button>

        {/* Dropdown popover using RAC Popover anchored to triggerRef */}
        <Popover
          triggerRef={triggerRef}
          isOpen={open}
          onOpenChange={setOpen}
          className={cn(
            'z-[var(--tvl-z-popover)] w-[--trigger-width] overflow-hidden',
            'rounded-[var(--tvl-r3)] border border-[var(--tvl-hairline)]',
            'bg-[var(--tvl-raised)] shadow-[var(--tvl-e2)]',
            'data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95',
            'data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95',
          )}
          offset={4}
        >
          {/* Search input */}
          <div className="border-b border-[var(--tvl-hairline)] px-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className={cn(
                'h-9 w-full bg-transparent text-sm text-[var(--tvl-ink)]',
                'placeholder:text-[var(--tvl-ink-3)] outline-none',
              )}
              aria-label="Search options"
            />
          </div>

          <ListBox<MultiSelectOption>
            aria-label="Options"
            items={filteredOptions}
            selectionMode="multiple"
            selectedKeys={new Set(value)}
            onSelectionChange={(keys) => {
              onValueChange?.(Array.from(keys as Set<string>));
            }}
            className="max-h-60 overflow-auto p-1 outline-none"
            renderEmptyState={() => (
              <div className="px-2 py-3 text-center text-xs text-[var(--tvl-ink-3)]">
                {emptyMessage}
              </div>
            )}
          >
            {(item) => {
              const isChecked = value.includes(item.value);
              return (
                <ListBoxItem
                  key={item.value}
                  id={item.value}
                  textValue={labelOf(item)}
                  isDisabled={
                    item.disabled ||
                    (!isChecked && !!maxSelected && value.length >= maxSelected)
                  }
                  className={cn(
                    'flex h-8 w-full cursor-pointer select-none items-center justify-between',
                    'rounded-[var(--tvl-r2)] px-2 text-sm text-[var(--tvl-ink)] outline-none',
                    'hover:bg-[var(--tvl-wash-hover)]',
                    'data-[focused]:bg-[var(--tvl-wash-hover)]',
                    isChecked && 'bg-[var(--tvl-selected)] font-medium',
                    'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                  )}
                >
                  <span className="flex-1 truncate">{labelOf(item)}</span>
                  {isChecked && (
                    <CheckLine size={14} color="var(--tvl-accent)" aria-hidden />
                  )}
                </ListBoxItem>
              );
            }}
          </ListBox>
        </Popover>
      </div>
    );
  },
);
MultiSelect.displayName = 'MultiSelect';
